/**
 * **Disposable fixture — diagnostics checks.**
 *
 * Which checks the collector actually runs, and what their commands really
 * print, are open questions for backend (`decisions.md`). These four are the
 * design's own — port listener, traffic capture, device reachability, test log
 * delivery — with authored results and authored raw output standing in for the
 * real thing.
 *
 * The checks are **derived from the Log Source's status** rather than authored
 * per source, so a diagnostics run can never contradict the status pill at the
 * top of the same page: a `receiving` source cannot report "no packets", and a
 * `silent` one cannot report a healthy capture.
 *
 * **Delete condition:** the first real diagnostics endpoint. Deleted together
 * with `collectors.ts`.
 *
 * **Not demo-world content** (ADR 0017) — see `collectors.ts`.
 */
import { CAPTURE_WINDOW_SECONDS } from '../capture-window'

import type { DiagnosticsCheck, LogSource } from '../types'

/**
 * The four checks for one source, given the optional device IP the operator
 * typed.
 *
 * `deviceIp` is what the reachability check is *for*: without it there is no
 * device to ping, so that check warns and says what would make it run rather
 * than silently reporting a pass it did not earn (P-17).
 *
 * Each check's identity (`id`, `name`, `command`) is stated once, and only the
 * three fields that depend on the world — status, result, raw output — branch.
 */
export function buildDiagnosticsChecks(
	source: LogSource,
	deviceIp: string,
): DiagnosticsCheck[] {
	const endpoint = `${source.transport}/${source.port}`
	const isReceiving = source.status === 'receiving'
	const device = deviceIp.trim()

	return [
		{
			id: 'port-listener',
			name: 'Port listener',
			command: 'ss -an',
			// Always a pass: the listener is ours. If the port were not bound the
			// source would not exist — the Add Log Source flow blocks the port
			// conflict that is the only way to get here with a dead listener.
			status: 'pass',
			result: `${endpoint} bound and receiving`,
			rawOutput: [
				`${source.transport}   UNCONN   0   0   0.0.0.0:${source.port}   0.0.0.0:*`,
				`${source.transport}6  UNCONN   0   0      [::]:${source.port}      [::]:*`,
			].join('\n'),
		},
		{
			id: 'traffic-capture',
			name: 'Traffic capture',
			command: 'tcpdump',
			status: isReceiving ? 'pass' : 'warn',
			result: isReceiving
				? `142 packets during ${CAPTURE_WINDOW_SECONDS}s capture`
				: `No packets during ${CAPTURE_WINDOW_SECONDS}s capture`,
			rawOutput: capturedPacketsOutput(isReceiving ? 142 : 0),
		},
		{
			id: 'device-reachability',
			name: 'Device reachability',
			command: 'ping / traceroute',
			status: device ? 'pass' : 'warn',
			result: device
				? `${device} — ping 1.2 ms · traceroute 3 hops`
				: 'No device IP given — enter one to test reachability',
			rawOutput: device
				? pingOutput(device)
				: 'Skipped: the collector was not told which device to reach.',
		},
		{
			id: 'test-log-delivery',
			name: 'Test log delivery',
			command: 'logger',
			// Independent of the device: this check sends a log the collector
			// generates itself, which is what makes it useful when the capture check
			// warns — it separates "our side is broken" from "your device is silent".
			status: 'pass',
			result: 'Generated test log reached AIR®',
			rawOutput: [
				`logger -n 127.0.0.1 -P ${source.port} -${source.transport === 'udp' ? 'd' : 'T'} "bitlyft-collector diagnostics test"`,
				'sent 1 message, acknowledged by AIR® ingest in 380 ms',
			].join('\n'),
		},
	]
}

/** `tcpdump`'s tail, which differs only in the count it reports. */
function capturedPacketsOutput(packets: number): string {
	return [
		'tcpdump: listening on eth0, link-type EN10MB (Ethernet), capture size 262144 bytes',
		`${packets} packets captured`,
		`${packets} packets received by filter`,
		'0 packets dropped by kernel',
	].join('\n')
}

/** `ping`'s output for a device that answered. */
function pingOutput(device: string): string {
	return [
		`PING ${device} (${device}) 56(84) bytes of data.`,
		`64 bytes from ${device}: icmp_seq=1 ttl=63 time=1.19 ms`,
		`--- ${device} ping statistics ---`,
		'3 packets transmitted, 3 received, 0% packet loss, time 2003ms',
	].join('\n')
}
